import React, { useState, useEffect, useRef } from "react";
import { Search, User as UserIcon, XCircle, ArrowLeft } from "lucide-react";
import { Button, Modal } from "antd";
import { useChatSocket } from "../../../services/support.service";
import MessageBubble from "./MessageBubble";
import MessageInput from "./MessageInput";
import DateSeparator from "./DateSeparator";
import ConnectionStatus from "./ConnectionStatus";
import StatusIndicator from "./StatusIndicator";
import { useChatMessages } from "../../../hooks/useChatMessages";
import { useFileUpload } from "../../../hooks/useFileUpload";
import { User, Message, Conversation } from "../../../types/support.types";
import StatsCards from "./StatsCards";
import { TitleArea } from "../../../components/Typography/titleArea";

interface AdminSupportScreenProps {
  currentUser: User;
}

const AdminSupportScreen: React.FC<AdminSupportScreenProps> = ({
  currentUser,
}) => {
  const [message, setMessage] = useState("");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [stats, setStats] = useState({
    active: 0,
    waiting: 0,
    closed: 0,
    total: 0,
  });
  const [uploading, setUploading] = useState(false);
  const [showMobileList, setShowMobileList] = useState(true);

  const { getChatSocket, isConnected } = useChatSocket("admin");

  const chatSocket = getChatSocket();

  const {
    messagesContainerRef,
    scrollToBottom,
    formatTime,
    groupMessagesByDate,
  } = useChatMessages(messages);

  const {
    selectedFile,
    setSelectedFile,
    handleFileSelect,
    handleRemoveFile,
    convertFileToBase64,
  } = useFileUpload(10);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!chatSocket || !isConnected) return;

    chatSocket.emit("chat:get-conversations", {
      adminId: parseInt(currentUser.id),
    });

    chatSocket.on("chat:conversations-loaded", (convs: Conversation[]) => {
      setConversations(convs);

      const activeCount = convs.filter((c) => c.status === "active").length;
      const waitingCount = convs.filter((c) => c.status === "waiting").length;
      const closedCount = convs.filter((c) => c.status === "closed").length;
      setStats({
        active: activeCount,
        waiting: waitingCount,
        closed: closedCount,
        total: convs.length,
      });
    });

    chatSocket.on("chat:new-conversation", () => {
      chatSocket.emit("chat:get-conversations", {
        adminId: parseInt(currentUser.id),
      });
    });

    chatSocket.on("chat:new-message", (newMessage: Message) => {
      if (
        selectedConversation &&
        newMessage.conversation_id === selectedConversation.id
      ) {
        setMessages((prev) => [...prev, newMessage]);
        scrollToBottom();

        if (newMessage.sender_type === "user") {
          chatSocket.emit("chat:mark-as-read", {
            conversationId: selectedConversation.id,
            userId: parseInt(currentUser.id),
          });

          setConversations((prevConvs) =>
            prevConvs.map((conv) =>
              conv.id === selectedConversation.id
                ? {
                    ...conv,
                    unread_count_admin: 0,
                    last_message: newMessage.message,
                  }
                : conv
            )
          );
        }
      } else {
        chatSocket.emit("chat:get-conversations", {
          adminId: parseInt(currentUser.id),
        });
      }
    });

    chatSocket.on("chat:messages-loaded", (data: { messages: Message[] }) => {
      setMessages(data.messages.reverse());

      if (selectedConversation) {
        setConversations((prevConvs) =>
          prevConvs.map((conv) =>
            conv.id === selectedConversation.id
              ? { ...conv, unread_count_admin: 0 }
              : conv
          )
        );
      }
    });

    chatSocket.on("chat:error", (error: { message: string }) => {
      console.error("Chat error:", error.message);
    });

    chatSocket.on("chat:message-sent", (sentMessage: Message) => {
      if (
        selectedConversation &&
        sentMessage.conversation_id === selectedConversation.id
      ) {
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === sentMessage.id);
          if (!exists) {
            return [...prev, sentMessage];
          }
          return prev;
        });
        scrollToBottom();
      }

      setConversations((prevConvs) =>
        prevConvs.map((conv) =>
          conv.id === sentMessage.conversation_id
            ? { ...conv, last_message: sentMessage.message }
            : conv
        )
      );
    });

    chatSocket.on(
      "chat:conversation-updated",
      (data: { conversationId: number; status: string }) => {
        setConversations((prevConvs) =>
          prevConvs.map((conv) =>
            conv.id === data.conversationId
              ? { ...conv, status: data.status }
              : conv
          )
        );

        if (selectedConversation?.id === data.conversationId) {
          setSelectedConversation((prev) =>
            prev ? { ...prev, status: data.status } : null
          );
        }
      }
    );

    return () => {
      chatSocket.off("chat:conversations-loaded");
      chatSocket.off("chat:new-conversation");
      chatSocket.off("chat:new-message");
      chatSocket.off("chat:messages-loaded");
      chatSocket.off("chat:messages-read");
      chatSocket.off("chat:error");
      chatSocket.off("chat:message-sent");
      chatSocket.off("chat:conversation-updated");
    };
  }, [chatSocket, isConnected, currentUser.id, selectedConversation]);

  const handleSelectConversation = (conv: Conversation) => {
    setSelectedConversation(conv);
    setMessages([]);
    setShowMobileList(false);

    setConversations((prevConvs) =>
      prevConvs.map((c) =>
        c.id === conv.id ? { ...c, unread_count_admin: 0 } : c
      )
    );

    if (!chatSocket) return;

    chatSocket.emit("chat:load-messages", {
      conversationId: conv.id,
      userId: parseInt(currentUser.id),
    });
  };

  const handleBackToList = () => {
    setShowMobileList(true);
    setSelectedConversation(null);
  };

  const handleCloseConversation = () => {
    if (!selectedConversation || !chatSocket) return;

    Modal.confirm({
      title: "Close Conversation",
      content:
        "Are you sure you want to close this conversation? The user will be notified.",
      okText: "Yes, Close",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      className: "vi-sup-modal",
      onOk: () => {
        chatSocket.emit("chat:close-conversation", {
          conversationId: selectedConversation.id,
        });
      },
    });
  };

  const handleSendMessage = async () => {
    if (
      !chatSocket ||
      (!message.trim() && !selectedFile) ||
      !selectedConversation
    )
      return;

    if (selectedConversation.status === "closed") {
      Modal.warning({
        title: "Conversation Closed",
        content:
          "This conversation has been closed. You cannot send messages to it.",
        className: "vi-sup-modal",
      });
      return;
    }

    setUploading(true);
    try {
      let imageData = null;
      let imageType = null;

      if (selectedFile) {
        imageData = await convertFileToBase64(selectedFile);
        imageType = selectedFile.type;
      }

      chatSocket.emit("chat:send-message", {
        conversationId: selectedConversation.id,
        message: message.trim() || (selectedFile ? "Attachment" : ""),
        senderId: parseInt(currentUser.id),
        senderType: "admin",
        image: imageData,
        imageType: imageType,
      });

      setMessage("");
      handleRemoveFile(fileInputRef);
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return formatTime(dateString);
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  const messageGroups = groupMessagesByDate(messages);

  const filteredConversations = conversations.filter(
    (conv) =>
      conv.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col w-full h-screen">
      <div
        className={`flex flex-col px-4 py-3 ${
          !showMobileList && selectedConversation ? "hidden md:flex" : "flex"
        }`}
      >
        <div className="flex items-center justify-between w-full mb-4">
          <TitleArea
            variant="page"
            title="Support"
            subTitleContent={<ConnectionStatus isConnected={isConnected} />}
          />
        </div>

        <hr className="hr-line w-full my-2" />

        <StatsCards stats={stats} />
      </div>

      <div className="vi-glass-card flex flex-1 overflow-hidden m-2 md:m-4 mb-4 md:mb-10">
        <div
          className={`${
            showMobileList ? "flex" : "hidden"
          } md:flex w-full md:w-80 bg-white border-r border-gray-200 flex-col`}
        >
          <div className="p-4 border-b border-gray-100">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search conversations"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <UserIcon size={48} className="mx-auto mb-2 opacity-30" />
                <p>No conversations found</p>
              </div>
            )}

            {filteredConversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => handleSelectConversation(conv)}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedConversation?.id === conv.id
                    ? "bg-blue-50 border-r-4 border-r-blue-500"
                    : ""
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold flex-shrink-0 relative">
                    {conv.user_name.charAt(0).toUpperCase()}
                    <div
                      className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                        conv.status === "active"
                          ? "bg-green-500"
                          : conv.status === "waiting"
                          ? "bg-yellow-500"
                          : "bg-gray-400"
                      }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium text-gray-900 truncate">
                        {conv.user_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(conv.last_message_at)}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-sm text-gray-500 truncate">
                        {conv.username}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 truncate mt-1">
                      {conv.last_message || "No messages yet"}
                    </div>
                    {conv.unread_count_admin > 0 && (
                      <span className="inline-block mt-2 bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1">
                        {conv.unread_count_admin}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className={`${
            !showMobileList ? "flex" : "hidden"
          } md:flex flex-1 flex-col min-w-0`}
        >
          {selectedConversation ? (
            <>
              <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 md:py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <button
                      onClick={handleBackToList}
                      className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <ArrowLeft size={20} />
                    </button>
                    <h1 className="text-lg md:text-xl font-semibold text-gray-900">
                      {selectedConversation.user_name}
                    </h1>
                    <StatusIndicator
                      status={selectedConversation.status}
                      showLabel={true}
                      size="sm"
                    />
                  </div>
                  {selectedConversation.status !== "closed" && (
                    <Button
                      danger
                      icon={<XCircle size={16} />}
                      onClick={handleCloseConversation}
                      size="small"
                      className="text-xs md:text-sm"
                    >
                      <span className="hidden sm:inline">
                        Close Conversation
                      </span>
                      <span className="sm:hidden">Close</span>
                    </Button>
                  )}
                </div>
              </div>

              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto bg-white"
              >
                <div className="w-full px-3 md:px-6 py-4 md:py-6">
                  {messages.length === 0 && (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-400">No messages yet</p>
                    </div>
                  )}

                  {messages.length > 0 && (
                    <>
                      {Object.entries(messageGroups).map(
                        ([dateLabel, dateMessages]) => (
                          <div key={dateLabel}>
                            <DateSeparator label={dateLabel} />

                            <div className="space-y-4 md:space-y-6 mb-6 md:mb-8">
                              {dateMessages.map((msg) => (
                                <MessageBubble
                                  key={msg.id}
                                  message={msg}
                                  isOwnMessage={msg.sender_type === "admin"}
                                  senderName={
                                    msg.sender_type === "admin"
                                      ? msg.sender_name || "Admin"
                                      : selectedConversation.user_name
                                  }
                                  senderInitial={selectedConversation.user_name
                                    .charAt(0)
                                    .toUpperCase()}
                                  formatTime={formatTime}
                                  isSystemMessage={
                                    msg.isSystem === 1 ||
                                    msg.sender_name === "System"
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        )
                      )}
                    </>
                  )}
                </div>
              </div>

              <MessageInput
                message={message}
                setMessage={setMessage}
                onSend={handleSendMessage}
                onFileSelect={handleFileSelect}
                selectedFile={selectedFile}
                onRemoveFile={() => handleRemoveFile(fileInputRef)}
                isConnected={isConnected}
                uploading={uploading}
                placeholder="Type your response..."
                attachButtonVariant="secondary"
                disabled={selectedConversation.status === "closed"}
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center text-gray-400 px-4">
                <UserIcon
                  size={48}
                  className="mx-auto mb-4 opacity-30 md:w-16 md:h-16"
                />
                <p className="text-base md:text-lg">
                  Select a conversation to start chatting
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupportScreen;
